import { type ReactElement, useRef, useState } from "react"
import styled, { css } from "styled-components"
import type { DetailSession } from "~/features/detail/types"
import {
  BackArrowIcon,
  ClosedCaptionIcon,
  ClosedCaptionOnIcon,
  PauseCircleIcon,
  PlayCircleIcon,
  SessionsIcon,
  SlidersIcon,
  TimerIcon,
} from "~/shared/ui/icons"
import type { ControlsVisibility } from "../hooks/useControlsVisibility"
import type { ThumbnailCue } from "../lib/thumbnailsVtt"
import { formatTimeRange } from "../lib/timecode"
import type { PlayerCommands, PlayerState } from "../types"
import { PlaybackSpeedPanel } from "./PlaybackSpeedPanel"
import { PlayerScrubBar } from "./PlayerScrubBar"
import { PlayerSessionRail } from "./PlayerSessionRail"

/**
 * `pointer-events` is the load-bearing rule in this file.
 *
 * The overlay covers the whole video so a click anywhere toggles playback, but it
 * must not swallow clicks meant for the controls sitting on top of it. So the
 * root passes pointer events through, the backdrop takes them for the toggle, and
 * every control layer takes them back.
 */
const Root = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  grid-template-rows: auto 1fr auto;
  pointer-events: none;
  z-index: ${({ theme }) => theme.zIndex.overlay};
`

const Backdrop = styled.button`
  position: absolute;
  inset: 0;
  padding: 0;
  border: none;
  background: none;
  cursor: default;
  pointer-events: auto;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.player.focusRing};
    outline-offset: -4px;
  }
`

const fade = css<{ $isVisible: boolean }>`
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  visibility: ${({ $isVisible }) => ($isVisible ? "visible" : "hidden")};
  transition: opacity 300ms ease-in-out, visibility 300ms ease-in-out;
`

const Scrim = styled.div<{ $isVisible: boolean; $edge: "top" | "bottom" }>`
  position: absolute;
  inset-inline: 0;
  ${({ $edge }) => ($edge === "top" ? "top: 0;" : "bottom: 0;")}
  height: ${({ $edge }) => ($edge === "top" ? "28%" : "48%")};
  background-image: ${({ theme, $edge }) => ($edge === "top" ? theme.playerGradient.top : theme.playerGradient.bottom)};
  ${fade}
`

/** The dark blanket the session browser drops over the video (node 400-2757). */
const Blanket = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  inset: 0;
  background-color: ${({ theme }) => theme.player.blanket};
  ${fade}
`

/*
 * Positioned so it paints above the scrims and the session blanket.
 *
 * Those three are absolutely positioned; these content rows are in-flow grid
 * items. Within one stacking context a positioned element paints above the
 * inline content of a non-positioned sibling regardless of DOM order, so the
 * 82%-opaque blanket was sitting on top of the session list and dulling white
 * text to grey. Giving the content a position and a z-index puts it back on top.
 */
const Header = styled.div<{ $isVisible: boolean }>`
  grid-row: 1;
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => `${theme.player.edgeBlock} ${theme.player.edgeInline} 0`};
  pointer-events: auto;
  ${fade}
`

const Titles = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  min-width: 0;
`

const Title = styled.h1<{ $isDimmed: boolean }>`
  font-size: clamp(${({ theme }) => theme.fontSize.xl}, 2.4vw, ${({ theme }) => theme.fontSize.display});
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  line-height: ${({ theme }) => theme.lineHeight.tight};
  color: ${({ theme, $isDimmed }) => ($isDimmed ? theme.player.textStrong : theme.player.textPrimary)};
  overflow-wrap: anywhere;
  transition: color 300ms ease-in-out;
`

const SubTitle = styled.p<{ $isDimmed: boolean }>`
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  line-height: ${({ theme }) => theme.lineHeight.tight};
  color: ${({ theme, $isDimmed }) => ($isDimmed ? theme.player.textStrong : theme.player.textPrimary)};
  transition: color 300ms ease-in-out;
`

const IconButton = styled.button<{ $isActive?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 2.125rem;
  height: 2.125rem;
  padding: 0;
  border: none;
  border-radius: ${({ theme }) => theme.radius.pill};
  background-color: ${({ theme, $isActive }) => ($isActive ? theme.player.brand : "transparent")};
  color: ${({ theme, $isActive }) => ($isActive ? theme.player.onBrand : theme.player.textPrimary)};
  cursor: pointer;
  transition: background-color 120ms ease-in-out, color 120ms ease-in-out;

  &:hover:not(:disabled) {
    background-color: ${({ theme, $isActive }) => ($isActive ? theme.player.brand : "rgb(255 255 255 / 0.18)")};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.player.focusRing};
    outline-offset: 2px;
  }
`

const BackButton = styled(IconButton)`
  margin-top: ${({ theme }) => theme.spacing.xxs};
`

/**
 * The centre play/pause control. Kept mounted while hidden so the transition runs
 * both ways, and pulled out of the tab order when hidden so a keyboard viewer
 * cannot land on an invisible button.
 */
const CentreControl = styled.div<{ $isVisible: boolean }>`
  grid-row: 2;
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  ${fade}
`

const CentreButton = styled.button`
  display: inline-flex;
  padding: 0;
  border: none;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: none;
  color: ${({ theme }) => theme.player.textPrimary};
  cursor: pointer;
  pointer-events: auto;
  filter: drop-shadow(0 0 4px rgb(0 0 0 / 0.7));

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.player.focusRing};
    outline-offset: 4px;
  }
`

/**
 * Fades as one block, so the session panel goes with the controls when the chrome
 * idles out rather than being left floating over the video. Browsing sessions
 * holds the chrome open, so the rail can never be hidden while in use.
 */
const Footer = styled.div<{ $isVisible: boolean }>`
  grid-row: 3;
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
  padding-bottom: ${({ theme }) => theme.player.edgeBlock};
  pointer-events: auto;

  /*
   * Load-bearing. A grid item defaults to min-width: auto, so it refuses to
   * shrink below its content — the session rail would widen this instead of
   * scrolling inside itself, and the overflow would be clipped by the stage with
   * no way to reach it.
   */
  min-width: 0;

  ${fade}
`

/** Carries the inline safe area the footer used to, so the rail can go full-bleed. */
const ControlsBlock = styled.div<{ $isVisible: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  padding-inline: ${({ theme }) => theme.player.edgeInline};
  ${fade}
`

const InfoRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.md};
`

const TimeLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  line-height: ${({ theme }) => theme.lineHeight.tight};
  color: ${({ theme }) => theme.player.textPrimary};
  font-variant-numeric: tabular-nums;
`

const IconGroup = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`

const SessionsPanel = styled.div<{ $isVisible: boolean }>`
  display: ${({ $isVisible }) => ($isVisible ? "block" : "none")};
  /* Same reason as Footer, one level down: a flex item also defaults to auto. */
  min-width: 0;
`

interface PlayerControlsProps {
  readonly title: string
  readonly subTitle: string
  readonly state: PlayerState
  readonly commands: PlayerCommands
  readonly visibility: ControlsVisibility
  readonly cues: readonly ThumbnailCue[]
  readonly sessions: readonly DetailSession[]
  readonly currentSessionId: string
  readonly sessionsTitle: string
  readonly onSelectSession: (sessionId: string) => void
  readonly onExit: () => void
}

/**
 * Every pixel of player chrome, from OTT nodes 131-2050, 400-3126 and 400-2757.
 *
 * It renders `state` and calls `commands`; it owns no playback state of its own,
 * so a change to how playback works stops at `usePlayerEngine` and a change to
 * how it looks stops here.
 */
export const PlayerControls = ({
  title,
  subTitle,
  state,
  commands,
  visibility,
  cues,
  sessions,
  currentSessionId,
  sessionsTitle,
  onSelectSession,
  onExit,
}: PlayerControlsProps): ReactElement => {
  const [isSpeedPanelOpen, setIsSpeedPanelOpen] = useState(false)
  const speedButtonRef = useRef<HTMLButtonElement | null>(null)

  const isPlaying = state.status === "playing"
  const isBrowsing = visibility.mode === "browsingSessions"
  const isScrubbing = visibility.mode === "scrubbing"
  const { isChromeVisible } = visibility

  // The centre control and the top titles step back while the viewer is scrubbing
  // or browsing, the way the TV player fades them to 0 and 0.5.
  const showCentre = isChromeVisible && !isBrowsing && !isScrubbing && state.status !== "loading"

  const closeSpeedPanel = () => {
    setIsSpeedPanelOpen(false)
    visibility.closeSettings()
    speedButtonRef.current?.focus()
  }

  const toggleSpeedPanel = () => {
    if (isSpeedPanelOpen) {
      closeSpeedPanel()
      return
    }

    setIsSpeedPanelOpen(true)
    visibility.openSettings()
  }

  return (
    <Root data-qa-hook="player-controls">
      {/* A click target, not a control. The centre button and the space bar are
          the real ways to toggle playback, so this stays out of the tab order and
          out of the accessibility tree rather than duplicating them. */}
      <Backdrop
        type="button"
        tabIndex={-1}
        aria-hidden="true"
        data-qa-hook="player-backdrop"
        onClick={() => {
          if (isBrowsing) {
            visibility.closeSessions()
            return
          }
          commands.togglePlay()
        }}
      />

      <Scrim $edge="top" $isVisible={isChromeVisible} />
      <Scrim $edge="bottom" $isVisible={isChromeVisible} />
      <Blanket $isVisible={isBrowsing} />

      <Header $isVisible={isChromeVisible}>
        <BackButton type="button" aria-label="Back to details" data-qa-hook="player-back" onClick={onExit}>
          <BackArrowIcon size={20} />
        </BackButton>

        <Titles>
          <Title $isDimmed={isBrowsing} data-qa-hook="player-title">
            {title}
          </Title>
          {subTitle ? (
            <SubTitle $isDimmed={isBrowsing} data-qa-hook="player-subtitle">
              {subTitle}
            </SubTitle>
          ) : null}
        </Titles>
      </Header>

      <CentreControl $isVisible={showCentre}>
        <CentreButton
          type="button"
          tabIndex={showCentre ? 0 : -1}
          aria-label={isPlaying ? "Pause" : "Play"}
          data-qa-hook="player-play-toggle"
          onClick={commands.togglePlay}
        >
          {isPlaying ? <PauseCircleIcon size={64} /> : <PlayCircleIcon size={64} />}
        </CentreButton>
      </CentreControl>

      <Footer $isVisible={isChromeVisible}>
        <ControlsBlock $isVisible={!isBrowsing}>
          <InfoRow>
            <TimeLabel data-qa-hook="player-timecode">
              {formatTimeRange(state.currentSeconds, state.durationSeconds)}
            </TimeLabel>

            <IconGroup>
              {/* First in the row: this navigates, where the three after it are
                  settings. Replaces the OTT frame's "All Sessions" text tab,
                  which sat below the scrub bar and read as a label rather than a
                  control. The heading it carried now sits inside the open panel,
                  as in node 400-2757. */}
              <IconButton
                type="button"
                $isActive={isBrowsing}
                aria-label={sessionsTitle}
                aria-expanded={isBrowsing}
                aria-controls="player-sessions"
                data-qa-hook="player-sessions-toggle"
                onClick={visibility.toggleSessions}
              >
                <SessionsIcon />
              </IconButton>

              {isSpeedPanelOpen ? (
                <PlaybackSpeedPanel
                  value={state.rate}
                  onChange={commands.setRate}
                  onClose={closeSpeedPanel}
                  qaHook="player-speed-panel"
                />
              ) : null}

              <IconButton
                ref={speedButtonRef}
                type="button"
                $isActive={isSpeedPanelOpen || state.rate !== 1}
                aria-label="Playback speed"
                aria-haspopup="dialog"
                aria-expanded={isSpeedPanelOpen}
                data-qa-hook="player-speed-toggle"
                onClick={toggleSpeedPanel}
              >
                <TimerIcon />
              </IconButton>

              {/* The frame's `sliders` control has no menu designed behind it yet —
                  quality and audio track are the obvious candidates. Disabled
                  rather than dropped, so the row still matches the design. */}
              <IconButton type="button" disabled aria-label="Settings (coming soon)" data-qa-hook="player-settings">
                <SlidersIcon />
              </IconButton>

              {state.captions.isAvailable ? (
                <IconButton
                  type="button"
                  $isActive={state.captions.isEnabled}
                  aria-label="Closed captions"
                  aria-pressed={state.captions.isEnabled}
                  data-qa-hook="player-captions-toggle"
                  onClick={commands.toggleCaptions}
                >
                  {state.captions.isEnabled ? <ClosedCaptionOnIcon /> : <ClosedCaptionIcon />}
                </IconButton>
              ) : null}
            </IconGroup>
          </InfoRow>

          <PlayerScrubBar
            currentSeconds={state.currentSeconds}
            durationSeconds={state.durationSeconds}
            bufferedSeconds={state.bufferedSeconds}
            cues={cues}
            onScrubStart={visibility.beginScrub}
            onScrubEnd={seconds => {
              commands.seekTo(seconds)
              visibility.endScrub()
            }}
            qaHook="player-scrub-bar"
          />
        </ControlsBlock>

        <SessionsPanel id="player-sessions" $isVisible={isBrowsing}>
          <PlayerSessionRail
            title={sessionsTitle}
            sessions={sessions}
            currentSessionId={currentSessionId}
            onSelectSession={onSelectSession}
            isOpen={isBrowsing}
            qaHook="player-sessions"
          />
        </SessionsPanel>
      </Footer>
    </Root>
  )
}
